import { getCookie } from 'public/assets/js/analytics/cookie';
import { inferMarketoUtmFields } from './inferMarketoUtmFields';

/**
 * Maps Marketo `valObj` keys to Conversion Forms SDK field UUIDs and submits
 * via `window.ConversionFormsV1.submit`.
 *
 * Form and field UUIDs come from the published Conversion forms for Marketo
 * 2006 (contact) and 2008 (gated/webinar, including
 * /fintech-predictions-2026-report/). Field UUIDs are per form: 2008 was
 * duplicated from 2006 so its older fields kept the same UUIDs, but fields
 * added to each form since then have their own. Never throws — dual-submit
 * is fire-and-forget; Marketo remains the source of truth.
 */

/**
 * Marketo records checkbox fields as `yes` / `no`, so the opt-in has to be sent
 * that way for its Conversion value to match the Marketo submission. The hidden
 * boolean keys (`hasAdBlocker`, `Is_Request_in_EEA_or_Unknown__c`, `coastDemo`)
 * stay `true` / `false`, which is what Marketo holds for them.
 */
const YES_NO_MARKETO_KEYS = new Set(['Marketing_Comms_Opt_in__c']);

export const MARKETO_CONVERSION_MIGRATION_FLAG = 'marketo-conversion-migration';

/**
 * Dual-submit only when both the Marketo form ID and path match.
 * Pathnames are compared without a trailing slash; locale prefixes still match.
 */
export const CONVERSION_DUAL_SUBMIT: Readonly<Record<number, string>> = {
  2006: '/contact',
};

/** Submit only to Conversion, skipping Marketo, matched the same way. */
export const CONVERSION_ONLY: Readonly<Record<number, string>> = {
  2008: '/fintech-predictions-2026-report',
};

export const CONVERSION_SUBMISSION_ONLY_FLAG = 'conversion-submission-only';

/**
 * Pages whose destination follows the `conversion-submission-only` flag: submit
 * only to Conversion when it is `true`, otherwise only to Marketo. Keyed by
 * Marketo form ID because only forms with a Conversion mapping can submit
 * there.
 *
 * Unlike the routes above these are exact pathnames (no trailing slash), so
 * the locale prefix is part of the entry and other locales are not matched.
 */
export const CONVERSION_SUBMISSION_ONLY_FLAG_PATHS: Readonly<
  Record<number, readonly string[]>
> = {
  2008: [
    '/en-eu/plaid-adyen-pay-by-bank-global-trends',
    '/en-eu/the-fintech-effect-report-2025',
    '/en-eu/instant-payments-in-europe-whitepaper',
    '/en-eu/fintech-predictions-2026-report',
    '/en-eu/state-of-intelligent-finance-report',
    '/en-eu/the-financial-wellbeing-revolution-report',
    '/en-eu/open-banking-new-payments-era',
    '/accenture-report-embedded-finance',
    '/beyond-the-credit-card',
    '/the-fintech-effect-report-2023',
    '/2023-fintech-predictions-whitepaper',
    '/forrester-wave-open-banking',
    '/2024-fintech-predictions-whitepaper',
    '/guide-top-trends-future-of-finance',
    '/gartner-automation-use-cases-lending-underwriting',
    '/plaid-adyen-pay-by-bank-global-trends',
    '/whats-new/fall-2025/credit-deep-dive',
    '/whats-new/fall-2025/fraud-deep-dive',
    '/whats-new/fall-2025/transfer-for-platform-deep-dive',
    '/the-fintech-effect-report-2025',
    '/2025-fintech-predictions-whitepaper',
  ],
};

const normalizePathname = (pathname: string): string => {
  const withoutQuery = pathname.split('?')[0]?.split('#')[0] ?? '';
  if (withoutQuery === '/' || withoutQuery === '') {
    return withoutQuery;
  }
  return withoutQuery.replace(/\/+$/, '');
};

const matchesRoute = (
  routes: Readonly<Record<number, string>>,
  pathname: string,
  marketoKey: number,
): boolean => {
  const allowedPath = routes[marketoKey];
  if (allowedPath == null) {
    return false;
  }
  const normalized = normalizePathname(pathname);
  return normalized === allowedPath || normalized.endsWith(allowedPath);
};

export const shouldDualSubmitConversion = (
  flag: unknown,
  pathname: string,
  marketoKey: number,
): boolean => {
  if (flag !== true) {
    return false;
  }
  return matchesRoute(CONVERSION_DUAL_SUBMIT, pathname, marketoKey);
};

export const isConversionSubmissionOnlyFlagPath = (
  pathname: string,
  marketoKey: number,
): boolean => {
  return (
    CONVERSION_SUBMISSION_ONLY_FLAG_PATHS[marketoKey]?.includes(
      normalizePathname(pathname),
    ) ?? false
  );
};

/**
 * Whether to skip Marketo and submit only to Conversion. Flag-controlled pages
 * are decided by the flag alone (anything but `true` keeps Marketo) and take
 * precedence over the unconditional `CONVERSION_ONLY` routes.
 */
export const shouldSubmitConversionOnly = (
  pathname: string,
  marketoKey: number,
  conversionSubmissionOnlyFlag: unknown = false,
): boolean => {
  if (isConversionSubmissionOnlyFlagPath(pathname, marketoKey)) {
    return conversionSubmissionOnlyFlag === true;
  }
  return matchesRoute(CONVERSION_ONLY, pathname, marketoKey);
};

/** Marketo form IDs → Conversion form IDs. */
export const MARKETO_TO_CONVERSION_FORM_ID: Record<number, string> = {
  2006: '3dbbabcb-3475-4a54-bd54-24e64cacbf21',
  2008: '5d206ba1-6dae-4d48-a8d6-d59ff07b3401',
};

/**
 * Marketo API names → Conversion form field UUIDs.
 *
 * `referrerUrl` is deliberately absent: it reads the same `referrer_url` cookie
 * as `referralURL`, so mapping both would send one value twice.
 *
 * A key is present only where that form publishes a matching field, because
 * a UUID the published form does not know is still accepted by the SDK,
 * still returns HTTP 200, and is then discarded — a dropped field is
 * impossible to tell from a delivered one. 2006 publishes no job_title; 2008
 * publishes no comments, employee-size, ad-blocker, or coast-demo field.
 */
export const MARKETO_TO_CONVERSION_FIELD_ID: Record<
  number,
  Record<string, string>
> = {
  2006: {
    Email: '7d8a7da9-0b21-4679-935d-6e00b92341bf',
    FirstName: 'd95ca467-3a42-4578-9831-142a691a31c2',
    LastName: '494dc96a-cae0-493c-9519-ef90ee8b0ec2',
    Company: 'f5a0daca-1e4f-4d35-9a8e-174ca0610104',
    Phone: 'd4b6438d-09ee-49fc-ac15-2bcf26a979f7',
    Country: '0c037456-10b4-4740-b3ee-9b3b4f40dcb4',
    State: '2d99d020-fa0d-41d8-bd17-06a11c166b49',
    Marketing_Comms_Opt_in__c: '060312ba-8c32-4d42-82d3-a9b6966f0dfc',
    Plaid_Comments__c: 'c2d2b2dc-98c5-4860-a29e-0a74fedc4a6c',
    EU_Employee_Estimate_Size__c: 'e79ac7a5-8629-4790-b813-363879f18f6c',
    Is_Request_in_EEA_or_Unknown__c: '1a55ba49-0d85-4e74-8adf-03767b8d6209',
    utm_source__c: 'c74ebf0d-72e0-44a9-9811-27470d6cf0ff',
    utm_medium__c: '15be2dde-185e-4393-8284-5033b37346ee',
    utm_campaign__c: '396f276e-07c0-4700-a9ca-15f6091766ab',
    utm_term__c: '57e561aa-52ec-4987-80e9-8350c52f048c',
    utm_content__c: '59acf58a-2f96-4661-aeda-7ca722420e0d',
    utm_creative__c: '20a42b63-debe-4865-8a37-87aceafb0447',
    entryURL: 'd0f36a37-2dc4-4e17-8b62-d99f215426c5',
    referralURL: 'e7d9e66b-87ab-4499-ba5f-20ea17937dd2',
    currentURL: 'ab3b9988-3812-454b-96b7-939effc97459',
    gclid__c: '1dbf55c0-01e8-4c52-a11a-ba04984923dc',
    Microsoft_Lead_ID__c: 'dfcdfe06-2e57-4672-8a9b-03dfad2ad9b2',
    hasAdBlocker: 'bf68f630-acae-480b-8bb1-92bcb65350e5',
    segmentajsanonymousid: 'c7687456-b21f-4214-aead-2cc93a58a482',
    coastDemo: 'a1ca884d-1311-465d-a97d-fa010f7587e6',
  },
  2008: {
    Email: '7d8a7da9-0b21-4679-935d-6e00b92341bf',
    FirstName: 'd95ca467-3a42-4578-9831-142a691a31c2',
    LastName: '494dc96a-cae0-493c-9519-ef90ee8b0ec2',
    Company: 'f5a0daca-1e4f-4d35-9a8e-174ca0610104',
    Phone: 'd4b6438d-09ee-49fc-ac15-2bcf26a979f7',
    Title: 'fcfd67a8-bd80-4bd4-8aec-a3b8e4303ba0',
    Country: '0c037456-10b4-4740-b3ee-9b3b4f40dcb4',
    State: '2d99d020-fa0d-41d8-bd17-06a11c166b49',
    Marketing_Comms_Opt_in__c: '060312ba-8c32-4d42-82d3-a9b6966f0dfc',
    Is_Request_in_EEA_or_Unknown__c: '1a55ba49-0d85-4e74-8adf-03767b8d6209',
    utm_source__c: '4dc95826-3760-4d73-ac45-559b6efdc037',
    utm_medium__c: '5dd72ca8-5e20-4a9f-a2fe-f200121ed730',
    utm_campaign__c: 'e1899868-b807-4794-badd-337731b75244',
    utm_term__c: '478f04c7-0426-4294-bae1-7aa60a4cfbcb',
    utm_content__c: '6f005e84-3270-41e2-94fc-f0c88f65db7e',
    utm_creative__c: '20a42b63-debe-4865-8a37-87aceafb0447',
    entryURL: 'd0f36a37-2dc4-4e17-8b62-d99f215426c5',
    referralURL: 'e7d9e66b-87ab-4499-ba5f-20ea17937dd2',
    currentURL: 'ab3b9988-3812-454b-96b7-939effc97459',
    gclid__c: '1dbf55c0-01e8-4c52-a11a-ba04984923dc',
    Microsoft_Lead_ID__c: 'dfcdfe06-2e57-4672-8a9b-03dfad2ad9b2',
    segmentajsanonymousid: 'c7687456-b21f-4214-aead-2cc93a58a482',
  },
};

export type ConversionSubmitDeps = {
  formIds?: Record<number, string>;
  fieldIds?: Record<number, Record<string, string>>;
  submit?: (formId: string, fields: Record<string, string>) => Promise<boolean>;
};

const isFilledId = (id: string | undefined): id is string => {
  return Boolean(id);
};

export const getConversionFormId = (
  marketoKey: number,
  formIds: Record<number, string> = MARKETO_TO_CONVERSION_FORM_ID,
): string | undefined => {
  const formId = formIds[marketoKey];
  return isFilledId(formId) ? formId : undefined;
};

export const coerceToConversionString = (
  value: unknown,
): string | undefined => {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  return undefined;
};

/**
 * Marketo's `vals()` can hand back the checkbox as a boolean, as `true` /
 * `false`, or already as `yes` / `no` depending on whether React or Marketo's
 * own autofill set it, and all of those mean the same thing to the opt-in
 * field. Anything else is passed through rather than guessed at.
 */
export const coerceToYesNo = (value: unknown): string | undefined => {
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }

  const coerced = coerceToConversionString(value);
  if (coerced == null) {
    return undefined;
  }

  switch (coerced.toLowerCase()) {
    case 'true':
    case 'yes':
      return 'yes';
    case 'false':
    case 'no':
      return 'no';
    default:
      return coerced;
  }
};

export const getConversionFieldIds = (
  marketoKey: number,
  fieldIds: Record<
    number,
    Record<string, string>
  > = MARKETO_TO_CONVERSION_FIELD_ID,
): Record<string, string> | undefined => {
  return fieldIds[marketoKey];
};

export const mapMarketoValObjToConversionFields = (
  marketoFormKey: number,
  valObj: Record<string, unknown>,
  fieldIds: Record<
    number,
    Record<string, string>
  > = MARKETO_TO_CONVERSION_FIELD_ID,
): Record<string, string> | null => {
  const formFieldIds = getConversionFieldIds(marketoFormKey, fieldIds);
  if (formFieldIds == null) {
    return null;
  }

  const emailFieldId = formFieldIds.Email;
  const email = coerceToConversionString(valObj.Email);
  if (!isFilledId(emailFieldId) || email == null) {
    return null;
  }

  const fields: Record<string, string> = {
    [emailFieldId]: email,
  };

  Object.entries(valObj).forEach(([marketoKey, value]) => {
    if (marketoKey === 'Email') {
      return;
    }
    const fieldId = formFieldIds[marketoKey];
    if (!isFilledId(fieldId)) {
      return;
    }
    const coerced = YES_NO_MARKETO_KEYS.has(marketoKey)
      ? coerceToYesNo(value)
      : coerceToConversionString(value);
    if (coerced == null) {
      return;
    }
    fields[fieldId] = coerced;
  });

  return fields;
};

/**
 * Mirrors `ConversionFormsV1.submit` from https://forms.conversion.ai/script.js
 * without loading the SDK, which only loads under Performance consent because
 * it also auto-tracks form submissions. Tracking/session cookies are sent only
 * with Performance consent; new identifiers are never generated.
 */
export const submitConversionFormDirectly = async (
  formId: string,
  fields: Record<string, string>,
  options: { includeAnalyticsIdentifiers?: boolean } = {},
): Promise<boolean> => {
  try {
    const body = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      body.set(key, value);
    });
    if (options.includeAnalyticsIdentifiers === true) {
      const trackingId = getCookie('_s__t');
      const sessionId = getCookie('_s__s');
      if (trackingId) {
        body.set('tracking_id', trackingId);
      }
      if (sessionId) {
        body.set('session_id', sessionId);
      }
    }
    body.set('page_url', window.location.origin + window.location.pathname);
    if (document.referrer) {
      body.set('referrer', document.referrer);
    }
    const utmFields: Record<string, string> = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key.startsWith('utm_') || key.startsWith('cai_')) {
        utmFields[key] = value;
      }
    });
    if (Object.keys(utmFields).length > 0) {
      body.set('utm_fields', JSON.stringify(utmFields));
    }
    const response = await fetch(
      `https://forms.conversion.ai/api/v1/forms/${formId}/submit`,
      { method: 'POST', keepalive: true, body },
    );
    return response.ok;
  } catch {
    return false;
  }
};

export const submitConversionForm = async (
  marketoKey: number,
  valObj: Record<string, unknown>,
  deps: ConversionSubmitDeps = {},
): Promise<boolean> => {
  try {
    const formId = getConversionFormId(marketoKey, deps.formIds);
    if (formId == null) {
      return false;
    }

    const fields = mapMarketoValObjToConversionFields(
      marketoKey,
      inferMarketoUtmFields(valObj),
      deps.fieldIds,
    );
    if (fields == null) {
      return false;
    }

    const submit =
      deps.submit ??
      (typeof window !== 'undefined'
        ? window.ConversionFormsV1?.submit
        : undefined);
    if (typeof submit !== 'function') {
      return false;
    }

    return Boolean(await submit(formId, fields));
  } catch {
    return false;
  }
};
